/**
 * Normalises any axios / fetch error into a flat string usable by toast.error
 * or other text rendering. Handles:
 *   - FastAPI 422: detail is a list of {type, loc, msg, input, ctx} objects
 *   - FastAPI HTTPException: detail is a plain string
 *   - axios network errors with no response
 *   - anything weird → fallback message
 *
 * Without this, calling toast.error(detail) when detail is an object array
 * tries to render an object as a React child and triggers React error #31
 * (blank screen). Always run user-facing errors through here.
 */
export function extractErrorMessage(err, fallback = 'Request failed') {
  // axios shape
  const data = err?.response?.data;
  if (data !== undefined && data !== null) {
    return formatDetail(data.detail, fallback) || formatDetail(data, fallback) || fallback;
  }
  if (typeof err === 'string') return err;
  if (err?.message) return err.message;
  return fallback;
}

function formatDetail(detail, fallback) {
  if (detail === undefined || detail === null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    // Pydantic v2 validation errors → each item has msg + loc
    const parts = detail
      .map((d) => {
        if (typeof d === 'string') return d;
        if (d && typeof d === 'object') {
          const where = Array.isArray(d.loc) ? d.loc.filter((x) => x !== 'body').join('.') : '';
          const msg = d.msg || d.message || '';
          return where ? `${where}: ${msg}` : msg;
        }
        return null;
      })
      .filter(Boolean);
    return parts.join('; ') || fallback;
  }
  if (typeof detail === 'object') {
    // Single object (rare) — pluck a known message field
    return detail.msg || detail.message || fallback;
  }
  return String(detail);
}
